import { AllProductTypes } from '@repo/payments-core';

import { OutlineEmailIcon, UserProfileIcon } from '@nw/shared/ui';
import { getURLSearchParam } from '@nw/shared/utils';

import {
  ASYNC_SELECT,
  BANNER,
  BRANCH_ASYNC_SELECT,
  CHECKBOX,
  COLLEGE,
  CONDITIONALLY_DEPENDENT_FIELD,
  CONTENT,
  COUNTRY_ASYNC_SELECT,
  DATE_PICKER,
  DISTRICT_ASYNC_SELECT,
  EMAIL,
  FILE_UPLOADER,
  FORM_FIELD_GROUP,
  FORM_VISIT_DATETIME,
  INFORMED_OTP_FIELD,
  MULTI_SELECT_CHECKBOX_GROUP,
  MULTIPLE_SELECT,
  NUMBER,
  PARAMS,
  PHONE_NUMBER,
  RADIO_GROUP,
  SELECT,
  SELECT_A_DATE_TO_BOOK_A_SLOT,
  SLOT_SELECTION_FIELD,
  STATE_ASYNC_SELECT,
  TEXT,
  TEXTAREA,
  USER_NAME,
  WHATSAPP_INFO_STATUS
} from '../../constants/FieldTypeConstants';
import BannerFieldModel from '../../stores/models/FieldModels/BannerField';
import BranchComboBoxField from '../../stores/models/FieldModels/BranchComboBoxField';
import CheckboxField from '../../stores/models/FieldModels/CheckboxField';
import ConditionallyDependentField from '../../stores/models/FieldModels/ConditionalDependentField';
import ContentField from '../../stores/models/FieldModels/ContentField';
import CountriesComboBoxField from '../../stores/models/FieldModels/CountriesComboBoxField';
import DatePickerFieldModel from '../../stores/models/FieldModels/DatePickerFieldModel';
import DistrictComboBoxFieldModel from '../../stores/models/FieldModels/DistrictComboBoxField';
import FileUploaderFieldModel from '../../stores/models/FieldModels/FileUploaderField';
import FormFieldGroup from '../../stores/models/FieldModels/FormFieldGroupField';
import FormVisitDateTimeField from '../../stores/models/FieldModels/FormVisitDateTime';
import InformedOTPFieldModel from '../../stores/models/FieldModels/InformedOTPfield';
import InstituteFormField from '../../stores/models/FieldModels/InstituteFormField';
import MultipleSelectFieldModel from '../../stores/models/FieldModels/MultipleSelectField';
import MultiSelectCheckboxGroupField from '../../stores/models/FieldModels/MultiSelectCheckboxGroupField';
import ParamsField from '../../stores/models/FieldModels/ParamsField';
import PhoneNumberField from '../../stores/models/FieldModels/PhoneNumberField';
import RadioGroupFormField from '../../stores/models/FieldModels/RadioGroupFormField';
import SelectField from '../../stores/models/FieldModels/SelectField';
import SlotSelectionField from '../../stores/models/FieldModels/SlotSelectionField';
import StatesComboBoxField from '../../stores/models/FieldModels/StatesComboBoxField';
import TextAreaField from '../../stores/models/FieldModels/TextareaField';
import TextFormField from '../../stores/models/FieldModels/TextFormField';
import { UserProfileModel } from '../../stores/models/UserProfileModel';
import {
  AllFormFieldModelsType,
  FormFieldsType
} from '../../types/FieldModelTypes';
import {
  getFieldInitialValueBasedOnFieldType,
  getFieldOptions
} from '../../utils/FieldUtils';

export const getFieldFromFactory = (
  field: FormFieldsType,
  userProfile: UserProfileModel | null,
  product?: AllProductTypes | null
): AllFormFieldModelsType => {
  const { type } = field;

  const params: URLSearchParams = getURLSearchParam();

  const initialValue =
    userProfile?.[field.field as keyof UserProfileModel] ?? '';

  const getInitialValueByCategory = (category: string) => {
    switch (category) {
      case 'program':
        return product?.program ?? '';
      case 'amount':
        return product?.currentPricingDetails.amountToBePaid ?? '';
      default:
        return initialValue;
    }
  };

  switch (type) {
    case USER_NAME: {
      const fullName = userProfile?.name ? `${userProfile.name}` : '';
      return new TextFormField(field, fullName, 'text', UserProfileIcon);
    }
    case TEXT: {
      const categoryInitialValue = getInitialValueByCategory(
        field.category ?? ''
      );
      return new TextFormField(field, categoryInitialValue as string);
    }
    case EMAIL: {
      const email =
        field.shouldPrefilledFromUserProfile === false
          ? ''
          : (userProfile && userProfile.email) || '';
      const Icon = OutlineEmailIcon;
      return new TextFormField(field, email, EMAIL, Icon);
    }
    case NUMBER:
      return new TextFormField(field, initialValue as string, NUMBER);
    case PHONE_NUMBER: {
      const phoneNumber = userProfile ? userProfile.phoneNumber : '';
      return new PhoneNumberField(field, phoneNumber, 'phone');
    }
    case SELECT: {
      const selectInitialValue = getFieldInitialValueBasedOnFieldType(
        SELECT,
        userProfile,
        field,
        initialValue
      );
      return new SelectField(field, selectInitialValue, getFieldOptions(field));
    }
    case STATE_ASYNC_SELECT:
      return new StatesComboBoxField(field, initialValue as string);
    case DISTRICT_ASYNC_SELECT:
      return new DistrictComboBoxFieldModel(field, initialValue as string);
    case INFORMED_OTP_FIELD:
      return new InformedOTPFieldModel(field);
    case CHECKBOX:
      return new CheckboxField(field);
    case WHATSAPP_INFO_STATUS:
      return new CheckboxField(field, true);
    case PARAMS:
      return new ParamsField(field, params.get(field.field));
    case COLLEGE:
      return new InstituteFormField(field, '');
    case RADIO_GROUP:
      return new RadioGroupFormField(field, [SELECT_A_DATE_TO_BOOK_A_SLOT]);
    case SLOT_SELECTION_FIELD: {
      return new SlotSelectionField(field, initialValue as string);
    }
    case CONDITIONALLY_DEPENDENT_FIELD:
      return new ConditionallyDependentField(field, initialValue as string);
    case MULTI_SELECT_CHECKBOX_GROUP:
      return new MultiSelectCheckboxGroupField(field);
    case BRANCH_ASYNC_SELECT:
      return new BranchComboBoxField(field, initialValue as string);
    case BANNER:
      return new BannerFieldModel(field);
    case MULTIPLE_SELECT: {
      return new MultipleSelectFieldModel(field, initialValue as string);
    }
    case CONTENT:
      return new ContentField(field, initialValue as string);
    case FORM_FIELD_GROUP:
      return new FormFieldGroup(field);
    case FILE_UPLOADER: {
      return new FileUploaderFieldModel(field);
    }
    case DATE_PICKER: {
      return new DatePickerFieldModel(
        field,
        (getFieldInitialValueBasedOnFieldType(
          DATE_PICKER,
          userProfile,
          field
        ) as string) ?? initialValue
      );
    }
    case COUNTRY_ASYNC_SELECT: {
      return new CountriesComboBoxField(field, initialValue as string);
    }
    case TEXTAREA:
      return new TextAreaField(field);
    case FORM_VISIT_DATETIME:
      return new FormVisitDateTimeField(field);
    default: {
      throw Error(`${field.type} is missing`);
    }
  }
};
